// Из кого состоит реестр: правая колонка «Людей», когда человек ещё не выбран.
//
// ## Почему здесь вообще что-то стоит
//
// При 1680 точках ширины колонка детали — 1024 точки, и до 04.09.2026 в ней стоял абзац на
// 420 точек по центру: «Человек не выбран. Выберите человека в списке слева». Девять десятых
// площади белые, и сказано в них ровно то, что человек и так видит.
//
// Образец, как надо, в продукте уже был: на пульте подотчёта вместо «выберите человека» стоит
// сводка за период, и это и есть «утро финдиректора, ноль кликов». Здесь то же самое, только
// вопрос другой: не «сколько денег», а «кто у нас есть».
//
// ## Почему счёт на месте, а не с сервера
//
// С 04.09.2026 реестр приезжает целиком, а не первыми пятьюстами. Значит все люди уже лежат в
// памяти экрана, и состав считается точно и без единого лишнего запроса. Пять запросов «дай
// счётчик по статусу» дали бы ровно те же числа и пять обращений к серверу.
//
// **Считается только когда приехали ВСЕ.** Если строк меньше, чем total, — состав был бы
// посчитан по куску и назван итогом. Это хуже пустой колонки: пустая колонка не врёт.

import { Link } from 'react-router';

import type { EmployeeOut, EmployeeStatus } from '@/shared/api/endpoints/people';
import { countLabel } from '@/shared/text/plural';
import * as ProgressBar from '@/shared/ui/progress-bar';

import { REGISTRY_TAB_TITLES } from '../registry-tabs';

const ЛЮДИ = ['человек', 'человека', 'человек'] as const;

/**
 * Порядок состояний — смысловой: сначала те, кто работает, в конце те, кого уже нет.
 *
 * Состояний шесть, и все шесть должны быть здесь. До 01.10.2026 не было `sick`: при первом же
 * больничном сумма строк состава перестала бы сходиться с числом людей над списком, и человек
 * прочёл бы это как «программа посчитала неверно». Пока больничных нет, строки просто нет.
 */
const ПОРЯДОК: readonly EmployeeStatus[] = [
  'on_shift',
  'between_shifts',
  'reserve',
  'leave',
  'sick',
  'fired',
];

export interface СоставРеестраProps {
  люди: readonly EmployeeOut[];
  /** Сколько людей в выборке всего. Не сходится с длиной списка — состав не показывается. */
  всего: number | undefined;
  /** Куда ведёт строка состояния: адрес с уже наложенным отбором. */
  ссылка: (статус: EmployeeStatus) => string;
  /**
   * Выборка сужена отбором или поиском.
   *
   * Заголовок от этого меняется, и это не мелочь: «Кто в реестре» над шестью найденными
   * Багровскими — неправда, а человек читает заголовок раньше чисел. Сам состав при этом
   * остаётся: разложение найденных по состояниям отвечает на тот же вопрос, только про
   * выборку.
   */
  сужено: boolean;
}

export function СоставРеестра({ люди, всего, ссылка, сужено }: СоставРеестраProps) {
  const посчитано = new Map<EmployeeStatus, number>();
  for (const человек of люди) {
    посчитано.set(человек.status, (посчитано.get(человек.status) ?? 0) + 1);
  }
  const строки = ПОРЯДОК
    .filter((статус) => (посчитано.get(статус) ?? 0) > 0)
    .map((статус) => ({
      статус,
      сколько: посчитано.get(статус) ?? 0,
    }));
  // Доля считается от САМОГО людного состояния, а не от всех: доля от всех на пяти состояниях
  // даёт четыре коротких огрызка рядом с одной длинной полосой, и сравнивать между собой
  // нечего. Та же причина, что у полос воронки найма.
  const максимум = Math.max(1, ...строки.map((строка) => строка.сколько));

  return (
    // Читаемая мера, а не вся ширина колонки: на широком мониторе деталь — 1024 точки, и
    // строка «На вахте … 162 … полоса» растянулась бы на всю. Глаз теряет начало следующей
    // строки, а число уезжает от своего названия на пол-экрана. Тот же токен, что у рядов
    // величин в сводке.
    <div className='flex max-w-metrics flex-col gap-5 p-5'>
      <div className='flex flex-col gap-1'>
        <h2 className='text-label-lg text-text-strong-950'>
          {сужено ? 'Кто в выборке' : 'Кто в реестре'}
        </h2>
        <p className='text-paragraph-sm text-text-sub-600'>
          {всего === undefined ? 'Считаем…' : countLabel(всего, ЛЮДИ)} · нажмите состояние, чтобы
          оставить в списке только их
        </p>
      </div>

      <table className='w-full'>
        <tbody>
          {строки.map(({ статус, сколько }) => (
            <tr key={статус} className='border-b border-stroke-soft-200 last:border-b-0'>
              <th scope='row' className='px-2 py-2.5 text-left font-normal'>
                <Link
                  to={ссылка(статус)}
                  className='text-label-sm text-primary-base underline тач:inline-flex тач:min-h-11 тач:items-center'
                >
                  {/* Слова вкладок («Межвахта», «Уволены»), а не названия состояний («Межвахтовый отдых»,
                      «Уволен»): строка работает так же, как вкладка над списком, и называться должна так
                      же. «Уволен 25» читалось как «один человек, 25» (живой проход по боевым данным
                      01.10.2026). */}
                  {REGISTRY_TAB_TITLES[статус]}
                </Link>
              </th>
              <td className='px-2 py-2.5 text-label-sm tabular-nums text-text-strong-950'>
                <span className='flex items-center gap-2'>
                  <span className='w-8 shrink-0 text-right'>{сколько}</span>
                  {/* Полоса повторяет число слева, а не добавляет к нему: ширину даёт величина,
                      краску — дизайн-система. Подписи у полосы нет намеренно. */}
                  <ProgressBar.Root
                    value={сколько}
                    max={максимум}
                    aria-hidden='true'
                    className='w-40'
                  />
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
